/* Collection group: storefront filtering and "Load more" (<facet-filters>), description read-more,
   the sub-collection chip row and zero-result search suggestions (<search-suggestions>). */
(() => {
  const T = window.Theme || {};
  const debounce = T.debounce || ((fn, ms = 250) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; });
  const parser = new DOMParser();
  // Touch: the sort select is invisible (16px, no iOS zoom) and its chosen option shows in .collection-sort__value
  const showSortValue = (sel) => { const v = sel.closest('.collection-sort')?.querySelector('.collection-sort__value'); if (v) v.textContent = sel.selectedOptions[0]?.textContent.trim() || ''; };
  const cache = new Map();

  /* ------------------------------------------------------------------ <facet-filters>
     Section Rendering API: GET <url>?section_id=<id> returns only this section, from which
     each [data-facet-region] is swapped in place. Filter and pagination links stay real <a href>. */
  /* Page key for the "Load more" memory: path + query without Shopify's own "_…" parameters (Safari comes back from
   * Back through shop.app's user recognition with ?_su_rec=…, a new navigation that drops history.state). */
  const pageKey = () => {
    const u = new URL(location.href);
    [...u.searchParams.keys()].filter((k) => k.startsWith('_')).forEach((k) => u.searchParams.delete(k));
    return u.pathname + u.search;
  };
  const MORE = 'harber:load-more';
  const saveMore = (v) => { try { sessionStorage.setItem(MORE, JSON.stringify(v)); } catch { /* storage blocked */ } };
  const readMore = () => { try { return JSON.parse(sessionStorage.getItem(MORE) || 'null'); } catch { return null; } };

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.form = this.querySelector('[data-facets-form]');
      this.home = this.querySelector('[data-facets-home]');
      this.sheet = this.querySelector('modal-dialog');
      this.sheetBody = this.querySelector('[data-facets-sheet-body]');
      this.onPriceInput = debounce(() => this.submit(), 600);
      this.expanded = new Set();

      this.addEventListener('change', (e) => this.onChange(e));
      this.addEventListener('input', (e) => { if (e.target.closest('[data-price-range]')) this.onPriceInput(); });
      this.addEventListener('submit', (e) => { e.preventDefault(); this.submit(); });
      this.addEventListener('click', (e) => this.onClick(e));

      if (this.sheet && this.form) {
        this.sheet.addEventListener('modal:open', () => this.sheetBody?.append(this.form));
        /* Only take the form back when the sheet really ended up closed: a close event can arrive late (iOS, or a
         * re-open of an already open sheet) and would otherwise empty the sheet that is showing */
        const dlg = this.sheet.querySelector('dialog');
        dlg?.addEventListener('close', () => setTimeout(() => { if (!dlg.open) this.home?.append(this.form); }, 0));
      }

      this.onPop = (e) => { if (e.state && e.state.facets) Promise.resolve(this.render(new URL(location.href), { push: false })).then(() => this.restoreMore()); };
      window.addEventListener('popstate', this.onPop);
      if (!history.state) history.replaceState({ facets: true }, '', location.href);
      /* The scroll position goes with the loaded pages (scroll restoration is manual once pages were loaded) */
      const keepY = () => {
        if (!history.state?.hbMore) return;
        history.replaceState({ ...history.state, hbY: scrollY }, '', location.href);
        const saved = readMore();
        if (saved?.key === history.state.hbMore.key) saveMore({ ...saved, y: scrollY, at: Date.now() });
      };
      this.onLeave = (e) => { if (e.type === 'pagehide' || e.target.closest?.('a[href]')) keepY(); };
      document.addEventListener('click', this.onLeave, true);
      window.addEventListener('pagehide', this.onLeave);
      this.restoreMore();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPop);
      document.removeEventListener('click', this.onLeave, true);
      window.removeEventListener('pagehide', this.onLeave);
    }

    onChange(e) {
      const t = e.target;
      if (t.matches('[data-sort-proxy]')) {
        showSortValue(t);
        const radio = this.form?.querySelector(`input[name="sort_by"][value="${CSS.escape(t.value)}"]`);
        if (radio) radio.checked = true;
        this.submit();
        return;
      }
      if (!t.closest('[data-facets-form]')) return;
      if (t.type === 'number') { this.onPriceInput(); return; }
      if (t.name === 'sort_by') this.syncSortProxy(t.value);
      this.submit();
    }

    onClick(e) {
      const more = e.target.closest('[data-facets-more]');
      if (more) { this.toggleMore(more); return; }
      const loadMore = e.target.closest('a[data-load-more]');
      if (loadMore && !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)) {
        e.preventDefault();
        this.loadMore(loadMore);
        return;
      }
      const a = e.target.closest('a[data-facet-link]');
      if (!a || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      const url = new URL(a.href, location.origin);
      if (url.origin !== location.origin) return;
      e.preventDefault();
      this.render(url, { scroll: !!a.closest('.pagination-nav') });
    }

    /* "Show N more" in long facet lists. The open state survives result swaps. */
    toggleMore(btn, open) {
      const list = document.getElementById(btn.getAttribute('aria-controls'));
      if (!list) return;
      const next = open ?? btn.getAttribute('aria-expanded') !== 'true';
      const flip = () => list.toggleAttribute('data-expanded');
      if (list.hasAttribute('data-expanded') !== next) {
        if (open === undefined && window.Theme?.expand) window.Theme.expand(list, flip, { collapse: !next, items: ':scope > li' });
        else flip();
      }
      btn.setAttribute('aria-expanded', String(next));
      btn.textContent = next ? btn.dataset.less : btn.dataset.more;
      const group = btn.closest('details[data-group]')?.dataset.group;
      if (group) this.expanded[next ? 'add' : 'delete'](group);
    }

    /* "Load more": fetch the next page of this section, append its grid items and swap the pager.
       Works for collections and search (both render collection-pagination inside [data-pager]). */
    async loadMore(btn) {
      if (this.loadingMore) return;
      if (!this.querySelector('[data-product-grid]') || !btn.closest('[data-pager]')) { location.assign(btn.href); return; }
      const url = new URL(btn.href, location.origin);
      url.searchParams.delete('section_id');
      this.loadingMore = true;
      btn.setAttribute('aria-busy', 'true');
      btn.classList.add('is-loading');
      try {
        const items = await this.appendPage(url);
        /* The URL is left as it was (?page=N would make a reload show only page N): the loaded pages are kept in
         * history.state instead, so Back/reload can load them again before the scroll position is restored. */
        const key = pageKey();
        const prev = history.state?.hbMore?.key === key ? history.state.hbMore.pages : [];
        const hbMore = { key, pages: [...prev, url.pathname + url.search] };
        history.replaceState({ ...(history.state || {}), hbMore }, '', location.href);
        saveMore({ ...hbMore, at: Date.now() });
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
        const first = items.map((li) => li.querySelector('a[href]:not([tabindex="-1"])')).find(Boolean);
        first?.focus({ preventScroll: true });
      } catch (err) {
        location.assign(url.pathname + url.search);
      } finally {
        this.loadingMore = false;
        btn.removeAttribute('aria-busy');
        btn.classList.remove('is-loading');
      }
    }

    /* Fetches one page of this section, appends its grid items and swaps the pager. Returns the appended items. */
    async appendPage(url) {
      const grid = this.querySelector('[data-product-grid]');
      const pager = this.querySelector('[data-pager]');
      if (!grid || !pager) throw new Error('No grid');
      const fetchUrl = new URL(url);
      fetchUrl.searchParams.set('section_id', this.sectionId);
      const r = await fetch(fetchUrl, { headers: { Accept: 'text/html' } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const doc = parser.parseFromString(await r.text(), 'text/html');
      const freshGrid = doc.querySelector('[data-product-grid]');
      if (!freshGrid) throw new Error('No grid');
      const items = [...freshGrid.children];
      grid.append(...items);
      const freshPager = doc.querySelector('[data-pager]');
      if (freshPager) {
        freshPager.classList.remove('collection-pager--deep');
        freshPager.querySelectorAll('a[href*="section_id="]').forEach((a) => {
          const u = new URL(a.href, location.origin);
          u.searchParams.delete('section_id');
          a.href = u.pathname + u.search;
        });
        pager.replaceWith(freshPager);
      } else {
        pager.remove();
      }
      T.reveal?.(grid);
      T.parallax?.(grid);
      this.dispatchEvent(new CustomEvent('collection:rendered', { bubbles: true }));
      return items;
    }

    /* Back or reload on a page where "Load more" was used (a page rebuilt from the server, not the back/forward
     * cache): load the same pages again, then go back to where the shopper was. */
    async restoreMore() {
      let more = history.state?.hbMore;
      let y = history.state?.hbY;
      if (!more) {
        /* Came back through a redirect that dropped history.state: use the copy in sessionStorage, only on Back/reload
         * (or that redirect) and only if recent, so a fresh visit to the collection starts at page 1 */
        const nav = performance.getEntriesByType?.('navigation')[0]?.type;
        const saved = readMore();
        if (saved && Date.now() - saved.at < 30 * 60e3 && (nav === 'back_forward' || nav === 'reload' || /[?&]_su_rec/.test(location.search))) {
          more = saved;
          y = saved.y;
        }
      }
      if (!more || more.key !== pageKey() || !more.pages?.length) return;
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      if (!history.state?.hbMore) history.replaceState({ ...(history.state || {}), hbMore: { key: more.key, pages: more.pages } }, '', location.href);
      try {
        for (const u of more.pages) await this.appendPage(new URL(u, location.origin));
      } catch { /* keep what loaded */ }
      if (typeof y === 'number') scrollTo(0, y);
    }

    params() {
      const p = new URLSearchParams();
      if (!this.form) return p;
      for (const [k, v] of new FormData(this.form)) if (String(v).trim() !== '') p.append(k, v);
      return p;
    }

    submit() {
      if (!this.form) return;
      const url = new URL(this.form.getAttribute('action') || location.pathname, location.origin);
      url.search = this.params().toString();
      this.render(url);
    }

    syncSortProxy(value) {
      const proxy = this.querySelector('[data-sort-proxy]');
      if (proxy && value) { proxy.value = value; showSortValue(proxy); }
    }

    async render(url, { push = true, scroll = false } = {}) {
      url.searchParams.delete('section_id');
      url.searchParams.delete('page_id');
      const key = url.pathname + url.search;
      const results = this.querySelector('[data-facet-region="results"]');
      this.abortCtrl?.abort();
      const ctrl = (this.abortCtrl = new AbortController());
      this.classList.add('is-loading');
      results?.setAttribute('aria-busy', 'true');
      try {
        let html = cache.get(key);
        if (!html) {
          const fetchUrl = new URL(url);
          fetchUrl.searchParams.set('section_id', this.sectionId);
          const r = await fetch(fetchUrl, { signal: ctrl.signal, headers: { Accept: 'text/html' } });
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          html = await r.text();
          cache.set(key, html);
        }
        if (ctrl.signal.aborted) return;
        this.swap(parser.parseFromString(html, 'text/html'));
        if (push && key !== location.pathname + location.search) history.pushState({ facets: true }, '', key);
        if (scroll) {
          const top = this.getBoundingClientRect().top + scrollY - (parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-h'), 10) || 64) - 16;
          scrollTo({ top, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        location.assign(key);
        return;
      } finally {
        if (this.abortCtrl === ctrl) {
          this.classList.remove('is-loading');
          results?.removeAttribute('aria-busy');
        }
      }
    }

    swap(doc) {
      const states = new Map();
      this.querySelectorAll('details[data-group]').forEach((d) => states.set(d.dataset.group, d.open));
      const focusedId = document.activeElement && this.contains(document.activeElement) ? document.activeElement.id : null;

      const regions = new Set([...this.querySelectorAll('[data-facet-region]'), ...document.querySelectorAll(`[data-facet-section="${CSS.escape(this.sectionId)}"]`)]);
      regions.forEach((el) => {
        const fresh = doc.querySelector(`[data-facet-region="${el.dataset.facetRegion}"]`);
        el.innerHTML = fresh ? fresh.innerHTML : '';
      });

      this.querySelectorAll('a[href*="section_id="]').forEach((a) => {
        const u = new URL(a.href, location.origin);
        u.searchParams.delete('section_id');
        a.href = u.pathname + u.search;
      });
      this.querySelectorAll('details[data-group]').forEach((d) => {
        if (states.has(d.dataset.group)) d.open = states.get(d.dataset.group);
        if (this.expanded.has(d.dataset.group)) {
          const btn = d.querySelector('[data-facets-more]');
          if (btn) this.toggleMore(btn, true);
        }
      });
      const checkedSort = this.form?.querySelector('input[name="sort_by"]:checked');
      if (checkedSort) this.syncSortProxy(checkedSort.value);
      if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });

      T.reveal?.(this);
      T.parallax?.(this);
      this.dispatchEvent(new CustomEvent('collection:rendered', { bubbles: true }));
    }
  }
  if (!customElements.get('facet-filters')) customElements.define('facet-filters', FacetFilters);

  /* ------------------------------------------------------------------ <collection-read-more> */
  class CollectionReadMore extends HTMLElement {
    connectedCallback() {
      const btn = this.querySelector('.collection-read-more__toggle');
      const body = this.querySelector('.collection-read-more__body');
      if (!btn || !body) return;
      /* The server clamps any description over ~100 characters; hide the toggle when the clamped
         text actually fits (short text on wide screens). */
      const fit = () => {
        if (btn.getAttribute('aria-expanded') === 'true') return;
        btn.hidden = body.scrollHeight <= body.clientHeight + 1;
      };
      fit();
      if ('ResizeObserver' in window) new ResizeObserver(fit).observe(body);
      btn.addEventListener('click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!expanded));
        btn.textContent = expanded ? btn.dataset.more : btn.dataset.less;
        /* Smooth height between the clamped lines and the full text, both ways (Theme.expand) */
        const flip = () => this.toggleAttribute('data-clamped');
        const thaw = this.freezeHero(body, expanded);
        if (window.Theme?.expand) window.Theme.expand(body, flip, { collapse: expanded }).then(() => { if (expanded) thaw(); });
        else { flip(); if (expanded) thaw(); }
      });
    }

    /* On a photo header the text sits centred and the photo covers the box, so growing the box moved the title
     * and re-zoomed the photo: the whole header seemed to move. While it opens (and until it closes again) the
     * title is pinned where it is and the photo keeps its size and position; the box just reveals more of it. */
    freezeHero(body, closing) {
      const head = this.closest('.collection-head--media');
      const img = head?.querySelector('.collection-head__media img');
      if (!head || !img) return () => {};
      const thaw = () => {
        head.style.removeProperty('align-content');
        head.style.removeProperty('padding-top');
        ['position', 'inset', 'top', 'left', 'width', 'height', 'max-width'].forEach((p) => img.style.removeProperty(p));
      };
      if (closing) return thaw;
      const copy = head.querySelector('.collection-head__copy');
      const H0 = head.offsetHeight;
      const W = head.offsetWidth;
      /* pin the text first (start-aligned at its current offset), then measure the opened header for real
       * in the same frame (no paint): the toggle, line wraps and the pin itself all add height */
      if (copy) {
        const before = copy.getBoundingClientRect().top;
        head.style.alignContent = 'start';
        head.style.paddingTop = `${copy.offsetTop}px`;
        const drift = before - copy.getBoundingClientRect().top;
        if (drift) head.style.paddingTop = `${copy.offsetTop + drift}px`;
      }
      this.removeAttribute('data-clamped');
      const H1 = Math.max(H0, head.offsetHeight) + 2;
      this.setAttribute('data-clamped', '');
      const ratio = (img.naturalHeight || 2) / (img.naturalWidth || 3);
      const shown = Math.max(W * ratio, H0); /* height the photo is drawn at now (object-fit: cover) */
      const h = Math.max(shown, H1);
      const w = h === shown && W * ratio >= H0 ? W : h / ratio;
      /* keep the photo where it is now (centred); only drop it lower if its bottom wouldn't reach the grown box */
      const top = Math.max((H0 - h) / 2, H1 - h);
      Object.assign(img.style, { position: 'absolute', inset: 'auto', top: `${Math.round(top)}px`, left: `${Math.round((W - w) / 2)}px`, width: `${Math.round(w)}px`, height: `${Math.round(h)}px`, maxWidth: 'none' });
      return thaw;
    }
  }
  if (!customElements.get('collection-read-more')) customElements.define('collection-read-more', CollectionReadMore);

  /* ------------------------------------------------------------------ <collection-chips>
     Scrolls the active chip into view horizontally (no vertical page movement, no layout shift). */
  class CollectionChips extends HTMLElement {
    connectedCallback() {
      const track = this.querySelector('[data-track]');
      const active = this.querySelector('[aria-current]');
      if (!track || !active) return;
      requestAnimationFrame(() => {
        const left = active.offsetLeft - (track.clientWidth - active.offsetWidth) / 2;
        if (left > 0) track.scrollLeft = left;
      });
    }
  }
  if (!customElements.get('collection-chips')) customElements.define('collection-chips', CollectionChips);

  /* ------------------------------------------------------------------ <search-extras>
     Search page: our own search over every text of the website (theme.js T.siteSearch, harberapp site search).
     data-mode="top": FAQ answers and devices above the results. data-mode="zero" (nothing found by Shopify): also the
     products and pages whose texts mention the words, with the sentence that matched. Stays hidden when empty. */
  class SearchExtras extends HTMLElement {
    async connectedCallback() {
      const S = T.siteSearch;
      const terms = (this.dataset.terms || '').trim();
      if (!S || terms.length < 2 || this.loaded) return;
      this.loaded = true;
      const d = await S.get(terms);
      if (!d) return;
      const zero = this.dataset.mode === 'zero';
      const el = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; };
      const link = (cls, url, k) => { const a = el('a', cls); a.href = S.href(url); a.dataset.k = k; return a; };
      const groups = [];
      const group = (title, cls, items) => { if (!items.length) return; const g = el('div', `ps__group ${cls}`); g.append(el('p', 'eyebrow', title), ...items); groups.push(g); };
      group(this.dataset.strAnswers, 'ps__answers', d.answers.slice(0, zero ? 4 : 3).map((x) => { const a = link('ps__answer', x.url, 'answer'); a.append(el('span', 'ps__answer-q', x.title), S.snippet(x.snippet)); return a; }));
      group(this.dataset.strDevice, 'ps__devices', d.devices.map((v) => {
        const a = link('ps__device', v.url, 'device');
        a.append(el('span', 'ps__device-name', v.name));
        if (v.products) a.append(el('span', 'ps__device-n', (this.dataset.strFit || '').replace('[count]', v.products)));
        return a;
      }));
      if (zero && d.products.length) {
        const ul = el('ul', 'ps__products');
        ul.setAttribute('role', 'list');
        d.products.forEach((p) => {
          const li = el('li');
          const a = link('ps__product', p.url, 'product');
          const media = el('span', 'ps__media media');
          if (p.image) { const img = el('img'); img.src = `${p.image}${p.image.includes('?') ? '&' : '?'}width=200`; img.alt = ''; img.width = 100; img.height = 100; img.loading = 'lazy'; media.append(img); }
          a.append(media, el('span', 'ps__title', p.title));
          if (S.hasMark(p.snippet)) a.append(S.snippet(p.snippet, 'ps__why'));
          li.append(a);
          ul.append(li);
        });
        group(this.dataset.strFound, 'search-extras__products', [ul]);
      }
      if (zero && d.pages.length) {
        const ul = el('ul', 'ps__links');
        ul.setAttribute('role', 'list');
        d.pages.forEach((p) => { const li = el('li'); const a = link('ps__page', p.url, p.kind); a.append(el('span', 'ps__page-title', p.title), S.snippet(p.snippet)); li.append(a); ul.append(li); });
        group(this.dataset.strPages, 'search-extras__pages', [ul]);
      }
      if (!groups.length) return;
      const inner = el('div', 'ps search-extras__inner');
      inner.append(...groups);
      this.replaceChildren(inner);
      this.hidden = false;
      T.revealItems?.(groups);
    }
  }
  // Samsung Internet (Insights → Errors, Nov 2026) said this name was taken although get() found nothing; the rest of the file must still run
  try { if (!customElements.get('search-extras')) customElements.define('search-extras', SearchExtras); } catch (e) { /* already defined */ }

  /* ------------------------------------------------------------------ <search-suggestions>
     Zero-result search: asks the predictive search endpoint (typo tolerant) for query suggestions
     and products for the same terms, and shows them as "Did you mean". Stays hidden when empty. */
  class SearchSuggestions extends HTMLElement {
    async connectedCallback() {
      const terms = (this.dataset.terms || '').trim();
      if (!terms || this.loaded) return;
      this.loaded = true;
      try {
        const route = (T.routes && T.routes.predictiveSearch) || '/search/suggest';
        const u = new URL(route, location.origin);
        u.searchParams.set('q', terms);
        u.searchParams.set('section_id', 'predictive-search');
        u.searchParams.set('resources[type]', 'query,product');
        u.searchParams.set('resources[limit]', '6');
        u.searchParams.set('resources[limit_scope]', 'each');
        const r = await fetch(u, { headers: { Accept: 'text/html' } });
        if (!r.ok) return;
        const root = parser.parseFromString(await r.text(), 'text/html').querySelector('[data-predictive-results]');
        if (!root) return;
        const norm = (s) => s.trim().toLowerCase();
        const queries = [...root.querySelectorAll('[data-ps-query]')].filter((a) => norm(a.dataset.text || a.textContent) !== norm(terms));
        const products = root.querySelector('[data-ps-products]');
        const qBox = this.querySelector('[data-suggest-queries]');
        const pBox = this.querySelector('[data-suggest-products]');
        if (qBox && queries.length) qBox.replaceChildren(...queries);
        if (pBox && products) pBox.replaceChildren(products);
        if ((qBox && qBox.children.length) || (pBox && pBox.children.length)) this.hidden = false;
      } catch (e) { /* suggestions are optional */ }
    }
  }
  if (!customElements.get('search-suggestions')) customElements.define('search-suggestions', SearchSuggestions);
})();
